<template>
  <div
    class="px-6 py-5 overflow-y-auto right-content-grid-vs h-[calc(100vh-309px)]"
  >
    <div class="w-full text-[#414141] font-sans">
      <h2 class="text-[1.25rem] leading-[1.75rem] mb-[1rem] font-semibold">
        Fundraising Talking Points
      </h2>

      <div v-if="isLoading" class="text-gray-600 font-sans py-4">
        <p>Loading data...</p>
      </div>
      <div
        v-else-if="!authStore.user?.organization_id"
        class="p-4 bg-yellow-50 font-sans border border-yellow-200 rounded-md mb-4"
      >
        <p class="text-amber-700">Unable to load organization data. Please try refreshing the page.</p>
        <button
          @click="authStore.fetchUserData"
          class="bg-amber-600 font-sans text-white px-4 py-1 rounded-md mt-2"
        >
          Retry Loading Organization Data
        </button>
      </div>
      <div v-else>
        <div class="w-full">
          <div class="overflow-auto pb-16 scroll-smooth" ref="contentWindow">
            <div class="mt-4">
              <div class="flex items-start text-gray-700 mt-2 font-bold">
                <span class="">1.</span>
                <p class="ml-1">Thank Donor for Meeting</p>
              </div>

              <div class="flex items-start text-gray-700 mt-2">
                <span class="font-bold">2.</span>
                <p class="ml-1">
                  <span class="font-bold">Aspirational Vision: </span>
                  {{ pitchInfo?.vision }}
                </p>
              </div>

              <div class="flex items-start text-gray-700 mt-2">
                <span class="font-bold">3.</span>
                <p class="ml-1">
                  <span class="font-bold">Mission: </span>
                  {{ pitchInfo?.mission }}
                </p>
              </div>

              <div class="items-start text-gray-700 mt-2">
                <p class="font-bold">4. Core Annual Programs</p>
                <div class="ml-3">
                  <ol type="a">
                    <li
                      class="mx-2"
                      v-for="(project, index) in (Array.isArray(pitchInfo?.core_programs) ? pitchInfo.core_programs : [])"
                      :key="index"
                    >
                      <span class="capitalize text-[#4F4F4F] font-semibold">
                        {{ String.fromCharCode(index + 97) }}.
                        {{ project?.program_name }}
                      </span>
                      <span v-if="project?.program_description">
                        - {{ project.program_description }}
                      </span>
                    </li>
                  </ol>
                </div>
              </div>

              <div class="flex items-start text-gray-700 mt-2">
                <span class="font-bold">5.</span>
                <p class="ml-1">
                  <span class="font-bold">Annual Operating Budget: </span>
                  ${{ formatNumber(year1TotalAnnualExpenses) }}
                </p>
              </div>

              <div class="items-start text-gray-700 mt-2">
                <div class="flex items-start">
                  <span class="font-bold">6.</span>
                  <p class="ml-1">
                    <span class="font-bold">Annual Revenue: </span>
                    ${{ formatNumber(year1TotalRevenue) }}
                  </p>
                </div>
                <div class="ml-3">
                  <ol type="a">
                    <li
                      class="mx-2"
                      v-for="(item, idx) in revenueCategoryBreakdown"
                      :key="`${item.label}-${idx}`"
                    >
                      {{ item.label }} - {{ formatPercentage(item.percentage) }}
                    </li>
                  </ol>
                </div>
              </div>

              <div class="items-start text-gray-700 mt-2">
                <p class="font-bold">7. New Projects</p>
                <div class="ml-3">
                  <ol type="a">
                    <li
                      class="mx-2"
                      v-for="(project, index) in (Array.isArray(pitchInfo?.projects) ? pitchInfo.projects : [])"
                      :key="index"
                    >
                      <span class="capitalize text-[#4F4F4F] font-semibold">
                        {{ String.fromCharCode(index + 97) }}.
                        {{ project?.project_name }}
                      </span>
                      <span v-if="project?.project_description">
                        - {{ project.project_description }}
                      </span>
                    </li>
                  </ol>
                </div>
              </div>

              <div class="items-start text-gray-700 mt-2">
                <div class="flex items-start">
                  <span class="font-bold">8. 3 Year Fundraising Goal</span>
                  <p class="ml-1">
                    <span class="">:
                      ${{ formatNumber(totalFundraisingGoalFor3Years) }}
                    </span>
                  </p>
                </div>
                <div class="ml-3">
                  <ol type="a">
                    <li class="mx-2">
                      Annual Fundraising Goal:
                      ${{ formatNumber(year1AnnualFundraisingGoal) }}
                    </li>
                    <li class="mx-2">
                      New Project Fundraising Goal:
                      ${{ formatNumber(totalNewProjectFundraisingGoalFor3Years) }}
                    </li>
                  </ol>
                </div>
              </div>

              <div class="items-start text-gray-700 mt-2">
                <span class="font-bold">9. Solicitation </span>
                <div class="ml-3">
                  <ol type="a">
                    <li class="mx-2">
                      a. How do you want the donor/prospect to support your
                      organization?
                    </li>
                    <li class="mx-2">b. What is the specific ask?</li>
                  </ol>
                </div>
              </div>

              <div class="flex items-start text-gray-700 mt-2">
                <span class="font-bold">10.</span>
                <p class="ml-1 font-bold">Thank you for Meeting</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useAuthStore } from "@/stores/auth";
import global from "@/global";
import { useStepValidation } from "@/composables/useStepValidation";
const isLoading = ref(true);
const errorMessage = ref("");
const contentWindow = ref(null);
const pitchInfo = ref({});
const year1TotalAnnualExpenses = ref(0);
const year1TotalRevenue = ref(0);
const totalFundraisingGoalFor3Years = ref(0);
const year1AnnualFundraisingGoal = ref(0);
const totalNewProjectFundraisingGoalFor3Years = ref(0);
const revenueCategoryBreakdown = ref([
  { label: "Philanthropic Contributions", percentage: 0 },
  { label: "Earned Revenue", percentage: 0 },
  { label: "Investment Income", percentage: 0 },
  { label: "Government Appropriations", percentage: 0 },
  { label: "In-Kind Goods/Services", percentage: 0 },
]);

// Fetching the auth store
const authStore = useAuthStore();

// Add validation function for the step
const validateStep = async () => {
  // Since this is a display-only page, we can always return true to allow navigation
  return true;
};

// Use the step validation composable
useStepValidation({
  initialValidState: true,
  validateFn: validateStep,
});

// Format number helper function
function formatNumber(value) {
  if (!value) return '0';
  return new Intl.NumberFormat('en-US').format(value);
}

function formatPercentage(value) {
  const numericValue = Number(value) || 0;
  return `${numericValue.toFixed(1)}%`;
}

// Fetching pitch training data
async function fetchPitchTrainingData() {
  try {
    const res = await fetch(`${global.API_URL}/pitch-training`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
        "Content-Type": "application/json",
      },
    });
    
    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}`);
    }
    
    const data = await res.json();
    pitchInfo.value = data.data;
  } catch (error) {
    console.error("Failed to fetch pitch training data:", error);
    errorMessage.value = "Failed to load pitch training data. Please try refreshing the page.";
  }
}

// Fetch Year 1 Annual Operating Budget Summary (Module 7)
async function fetchYear1AnnualOperatingBudgetSummary() {
  try {
    const res = await fetch(`${global.API_URL}/total_fundraising_goal/annual_operating_budget_summary_year_1`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
      },
    });

    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}`);
    }

    const data = await res.json();
    const revenueGroupSums = data.revenue_year_1_grouped?.group_sums || {};
    const categoryLabels = [
      "Philanthropic Contributions",
      "Earned Revenue",
      "Investment Income",
      "Government Appropriations",
      "In-Kind Goods/Services",
    ];

    year1TotalAnnualExpenses.value = data.total_annual_expenses_year_1 || 0;
    year1TotalRevenue.value = data.total_revenue_year_1 || 0;

    const groupedTotal = categoryLabels.reduce(
      (sum, label) => sum + (Number(revenueGroupSums[label]) || 0),
      0
    );
    const valuesLookLikePercentages =
      groupedTotal > 0 && Math.abs(groupedTotal - 100) < 1.5;

    revenueCategoryBreakdown.value = categoryLabels.map((label) => {
      const rawValue = Number(revenueGroupSums[label]) || 0;
      const percentage = valuesLookLikePercentages
        ? rawValue
        : year1TotalRevenue.value > 0
          ? (rawValue / year1TotalRevenue.value) * 100
          : 0;

      return { label, percentage };
    });
  } catch (error) {
    console.error("Failed to fetch Year 1 annual operating budget summary:", error);
    year1TotalAnnualExpenses.value = 0;
    year1TotalRevenue.value = 0;
    revenueCategoryBreakdown.value = revenueCategoryBreakdown.value.map((item) => ({
      ...item,
      percentage: 0,
    }));
  }
}

// Fetch Module 7 annual fundraising goals data to match the "Annual Fundraising Goals (3 Years)" page
async function fetchAnnualFundraisingGoalsData() {
  try {
    const res = await fetch(`${global.API_URL}/total_fundraising_goal/annual_fundraising_goals_3years`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
      },
    });

    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}`);
    }

    const data = await res.json();
    const revenue = data.revenue_sums_by_type_and_year || {};
    const year1Expenses = Number(data.total_annual_expenses_year_1) || 0;
    const year1EarnedRevenue = Number(revenue["Earned Revenue"]?.year_1) || 0;
    const year1InvestmentIncome = Number(revenue["Investment Income"]?.year_1) || 0;
    const year1GovAppropriations = Number(revenue["Government Appropriations"]?.year_1) || 0;
    const year1InKindGoods = Number(revenue["In-Kind Goods/Services"]?.year_1) || 0;

    year1AnnualFundraisingGoal.value =
      year1Expenses -
      (year1EarnedRevenue + year1InvestmentIncome + year1GovAppropriations + year1InKindGoods);
  } catch (error) {
    console.error("Failed to fetch annual fundraising goals data:", error);
    year1AnnualFundraisingGoal.value = 0;
  }
}

// Fetch 3-year fundraising goal summary (Module 10 / Module 9 references)
async function fetchFundraisingGoalSummaryData() {
  try {
    const res = await fetch(`${global.API_URL}/total_fundraising_goal/total_fundraising_goal_for_3_years`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
      },
    });

    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}`);
    }

    const data = await res.json();
    totalFundraisingGoalFor3Years.value = data.final_total_fundraising_goal_for_3_years || 0;
    totalNewProjectFundraisingGoalFor3Years.value = data.total_new_project_fundraising_goal_for_3_years || 0;
  } catch (error) {
    console.error("Failed to fetch fundraising goal summary data:", error);
    totalFundraisingGoalFor3Years.value = 0;
    totalNewProjectFundraisingGoalFor3Years.value = 0;
  }
}

onMounted(async () => {
  try {
    isLoading.value = true;
    errorMessage.value = "";

    await authStore.fetchUserData();

    if (!authStore.user?.organization_id) {
      errorMessage.value = "Unable to load organization data. Please try refreshing the page.";
      console.error("No organization ID available");
      return;
    }

    await Promise.all([
      fetchPitchTrainingData(),
      fetchYear1AnnualOperatingBudgetSummary(),
      fetchAnnualFundraisingGoalsData(),
      fetchFundraisingGoalSummaryData(),
    ]);

  } catch (error) {
    console.error("Error during component initialization:", error);
    errorMessage.value = "An error occurred while loading the data. Please try refreshing the page.";
  } finally {
    isLoading.value = false;
  }
});
</script>

<style scoped>
.answer-err-enter-active,
.answer-err-leave-active {
  transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
}

.answer-err-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.answer-err-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style> 
